/**
 * Plaid security utilities for HMAC request signing.
 *
 * This module provides cryptographic functions for securing Plaid API requests.
 * Clients sign requests with a device_secret that only they possess, and the
 * server verifies signatures using a stored hash.
 *
 * Security model:
 * 1. Client generates device_secret alongside device_uuid on first Plaid connection
 * 2. Client computes device_secret_hash = SHA256(device_secret)
 * 3. Client sends device_secret_hash to server during token exchange
 * 4. Server stores only the hash
 * 5. For each request, client computes: HMAC-SHA256(device_secret_hash, payload|timestamp)
 * 6. Server verifies using stored hash
 */

import { Preferences } from '@capacitor/preferences';

const PLAID_DEVICE_SECRET_KEY = 'plaid_device_secret';

/**
 * Generate a cryptographically random device secret.
 * Uses Web Crypto API for secure random number generation.
 *
 * @returns A 32-byte hex-encoded secret (64 characters)
 */
export const generateDeviceSecret = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Compute SHA-256 hash of a string.
 * Used to hash the device secret before sending to server.
 *
 * @param data - The string to hash
 * @returns Hex-encoded SHA-256 hash (64 characters)
 */
export const sha256Hash = async (data: string): Promise<string> => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Compute HMAC-SHA256 signature for a request.
 *
 * @param key - The HMAC key (device_secret_hash)
 * @param message - The message to sign (payload|timestamp)
 * @returns Hex-encoded HMAC-SHA256 signature
 */
export const computeHmacSignature = async (key: string, message: string): Promise<string> => {
  const encoder = new TextEncoder();

  // Import the key for HMAC
  const keyBuffer = encoder.encode(key);
  const cryptoKey = await crypto.subtle.importKey('raw', keyBuffer, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);

  // Sign the message
  const messageBuffer = encoder.encode(message);
  const signatureBuffer = await crypto.subtle.sign('HMAC', cryptoKey, messageBuffer);

  // Convert to hex string
  const signatureArray = Array.from(new Uint8Array(signatureBuffer));
  return signatureArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Get or create a persistent device secret.
 * This secret is generated once and stored locally.
 * It's only lost if the user deletes the app.
 *
 * @returns Object containing deviceSecret and deviceSecretHash
 */
export const getOrCreateDeviceSecret = async (): Promise<{
  deviceSecret: string;
  deviceSecretHash: string;
}> => {
  const existing = await Preferences.get({ key: PLAID_DEVICE_SECRET_KEY });

  if (existing.value) {
    const deviceSecretHash = await sha256Hash(existing.value);
    return {
      deviceSecret: existing.value,
      deviceSecretHash,
    };
  }

  // Generate new secret
  const newSecret = generateDeviceSecret();
  await Preferences.set({ key: PLAID_DEVICE_SECRET_KEY, value: newSecret });

  const deviceSecretHash = await sha256Hash(newSecret);
  return {
    deviceSecret: newSecret,
    deviceSecretHash,
  };
};

/**
 * Get the device secret hash if it exists.
 * Returns null if no secret has been created yet.
 */
export const getDeviceSecretHash = async (): Promise<string | null> => {
  const existing = await Preferences.get({ key: PLAID_DEVICE_SECRET_KEY });
  if (!existing.value) {
    return null;
  }
  return sha256Hash(existing.value);
};

/**
 * Convert payload to compact JSON string matching Python's json.dumps format.
 * Uses compact separators (",", ":") without spaces after : or ,
 *
 * @param payload - The payload object or string
 * @returns Compact JSON string
 */
const toCompactJson = (payload: string | Record<string, unknown>): string => {
  if (typeof payload === 'string') {
    return payload;
  }
  // Sort keys alphabetically to match Python's json.dumps(sort_keys=True)
  const sortedKeys = Object.keys(payload).sort();
  const sorted = sortedKeys.reduce(
    (obj, key) => {
      obj[key] = payload[key];
      return obj;
    },
    {} as Record<string, unknown>,
  );
  // JSON.stringify produces compact output (no spaces) matching Python's json.dumps(separators=(",", ":"))
  return JSON.stringify(sorted);
};

/**
 * Sign a request payload for HMAC authentication.
 *
 * @param payload - The request payload (JSON string or object)
 * @returns Object containing signature headers, or null if no secret exists
 */
export const signRequest = async (
  payload: string | Record<string, unknown>,
): Promise<{
  signature: string;
  timestamp: number;
  deviceSecretHash: string;
} | null> => {
  const existing = await Preferences.get({ key: PLAID_DEVICE_SECRET_KEY });
  if (!existing.value) {
    return null;
  }

  const deviceSecret = existing.value;
  const deviceSecretHash = await sha256Hash(deviceSecret);
  const timestamp = Math.floor(Date.now() / 1000);

  // Normalize payload to compact JSON string matching Python's format
  const payloadString = toCompactJson(payload);

  // Create message to sign: payload|timestamp
  const message = `${payloadString}|${timestamp}`;

  // Sign using the hash as the HMAC key (matches server-side verification)
  const signature = await computeHmacSignature(deviceSecretHash, message);

  return {
    signature,
    timestamp,
    deviceSecretHash,
  };
};

/**
 * Create headers for a signed request.
 *
 * @param deviceUuid - The device UUID
 * @param payload - The request payload
 * @returns Headers object with HMAC signature headers, or empty object if not signed
 */
export const createSignedHeaders = async (
  deviceUuid: string,
  payload: string | Record<string, unknown>,
): Promise<Record<string, string>> => {
  const signedData = await signRequest(payload);

  if (!signedData) {
    // No secret available, return minimal headers
    return {
      'X-Device-UUID': deviceUuid,
    };
  }

  return {
    'X-Device-UUID': deviceUuid,
    'X-Timestamp': signedData.timestamp.toString(),
    'X-Signature': signedData.signature,
  };
};

/**
 * Clear the device secret (only do this when completely resetting).
 * WARNING: This will make existing connections inaccessible if HMAC is enforced.
 */
export const clearDeviceSecret = async (): Promise<void> => {
  await Preferences.remove({ key: PLAID_DEVICE_SECRET_KEY });
};
